/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useMemo } from 'react';
import moment, { Moment } from 'moment';
import CachedIcon from '@mui/icons-material/Cached';
import InputAdornment from '@mui/material/InputAdornment';
import MUITextField from '@mui/material/TextField';
import AdapterMoment from '@mui/lab/AdapterMoment';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DatePicker, { DatePickerProps } from '@mui/lab/DatePicker';
import { DatePickerValue, InputValue } from './types';
import { VALIDATORS } from '../../utils/validate-form-fields';

type OmittedDatePickerProps = Omit<DatePickerProps, 'renderInput' | 'onChange'>
type BlurEvent = React.FocusEvent<HTMLInputElement>

interface Props extends OmittedDatePickerProps {
  hidden?: boolean,
  hasRRule: boolean,
  value: { textValue: string },
  onChange: (date: InputValue) => void
}

const formatSystemDate = (date: DatePickerValue) => moment(date).format('YYYY-MM-DD');

const DateField: React.FC<Props> = ({ ...props }) => {
  const [value, setValue] = useState<DatePickerValue>(props.value.textValue);
  const [error, setError] = useState(false);
  const rrule = useMemo(() => props.hasRRule, [props.hasRRule]);
  const format = { textValue: '' };
  const clearError = () => error && setError(false);

  useEffect(() => { setValue(props.value.textValue); }, [props.value]);

  const handleChange = (date: DatePickerValue) => {
    if ((date as Moment)?.isValid()) {
      clearError();
      setValue(date);
      format.textValue = formatSystemDate(date);
    } else if (date != null) {
      format.textValue = VALIDATORS.DATE;
    } else {
      format.textValue = '';
    }

    props.onChange(format);
  };

  const handleBlur = (event: BlurEvent) => {
    const dateIsValid = moment(event.target.value, 'MM/DD/YYYY', true).isValid();

    if (dateIsValid || event.target.value === '') {
      clearError();
    } else {
      setError(true);
    }
  };

  const startAdornment = {
    startAdornment: (
      <InputAdornment position="start">
        <CachedIcon />
      </InputAdornment>
    ),
  };

  return (
    <>
      {!props.hidden && (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            label={props.label}
            disabled={props.disabled}
            value={value}
            onChange={handleChange}
            renderInput={(params) => (
              <MUITextField
                {...params}
                fullWidth
                error={error}
                {...(error && {
                  helperText: 'This date is invalid.',
                })}
                InputProps={{
                  ...params.InputProps,
                  ...(rrule && value ? startAdornment : {}),
                  onBlur: handleBlur,
                }}
              />
            )}
          />
        </LocalizationProvider>
      )}
    </>
  );
};

export default DateField;
